import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REGISTRATION_PAYMENT_LABELS,
  REGISTRATION_PAYMENT_STATUSES,
  REGISTRATION_STATUSES,
  type ContributionPersonRef,
  type EventWorldScope,
  type RegistrationAdminView,
  type RegistrationPaymentStatus,
  type RegistrationStatus,
  type WineCategoryView,
} from '@gwc/shared';
import {
  completeWine,
  draftFromView,
  isDraftEmpty,
  rsvpApi,
  type AdminRegisterBody,
  type AdminUpdateRegistrationBody,
  type StructuredWineDraft,
} from '../api/rsvp';
import { tastingsApi } from '../api/tastings';
import { ApiError } from '../lib/api';
import { ImageInput } from './ImageInput';
import { PersonPicker } from './PersonPicker';
import { StructuredWineFields } from './StructuredWineFields';
import { Button, Dialog } from './ui';

const STATUS_LABELS: Record<RegistrationStatus, string> = {
  registered: 'Attending',
  waitlist: 'Waitlist',
  cancelled: 'Cancelled',
};

/** What an admin may set when registering someone (the rest follow from events). */
const CREATE_PAYMENT_STATUSES = ['not_required', 'unpaid', 'paid', 'waived'] as const;

/**
 * Register someone on their behalf (a member who phoned in, or a guest added as
 * a person) — or correct an existing registration: wine, category, label,
 * notes, payment and status. Admin edits bypass the invite and dues gates and
 * may override the seat limit or a full category.
 */
export function RegistrationEditorDialog({
  open,
  onClose,
  eventId,
  registrationId,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Edit this registration; omit to register someone new. */
  registrationId?: string;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const editing = !!registrationId;
  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => tastingsApi.get(eventId),
    enabled: open,
  });
  const categoriesQuery = useQuery({
    queryKey: ['wine-categories', eventId],
    queryFn: () => rsvpApi.categories(eventId),
    enabled: open,
    retry: false,
  });
  const registrationsQuery = useQuery({
    queryKey: ['registrations', eventId],
    queryFn: () => rsvpApi.registrations(eventId),
    enabled: open && editing,
  });
  const existing = useMemo(
    () => registrationsQuery.data?.find((r) => r.id === registrationId) ?? null,
    [registrationsQuery.data, registrationId],
  );

  const event = eventQuery.data?.event;
  const ready = !!event && (!editing || !!existing);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        editing
          ? existing
            ? `${existing.firstName} ${existing.lastName}`
            : 'Registration'
          : 'Register someone'
      }
      description={
        editing
          ? 'Correct their wine, category, payment or status.'
          : 'For a member who phoned in, or a guest you have added as a person.'
      }
      size="lg"
    >
      {!ready ? (
        <p className="text-sm text-cream-300/60">
          {registrationsQuery.isError || eventQuery.isError ? 'Could not load this registration.' : 'Loading…'}
        </p>
      ) : (
        <EditorForm
          key={existing?.id ?? 'new'}
          eventId={eventId}
          existing={existing}
          worldScope={event.worldScope ?? 'both'}
          requiresPayment={event.requiresPayment ?? false}
          categories={categoriesQuery.data ?? []}
          onCancel={onClose}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['registrations', eventId] });
            void qc.invalidateQueries({ queryKey: ['wine-categories', eventId] });
            void qc.invalidateQueries({ queryKey: ['rsvp', eventId] });
            void qc.invalidateQueries({ queryKey: ['attendees', eventId] });
            onSaved();
            onClose();
          }}
        />
      )}
    </Dialog>
  );
}

function EditorForm({
  eventId,
  existing,
  worldScope,
  requiresPayment,
  categories,
  onCancel,
  onSaved,
}: {
  eventId: string;
  existing: RegistrationAdminView | null;
  worldScope: EventWorldScope;
  requiresPayment: boolean;
  categories: WineCategoryView[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const initialWine = useMemo(() => draftFromView(existing?.wine), [existing]);
  const [person, setPerson] = useState<ContributionPersonRef | null>(null);
  const [status, setStatus] = useState<RegistrationStatus>(existing?.status ?? 'registered');
  const [wine, setWine] = useState<StructuredWineDraft>(initialWine);
  const [categoryId, setCategoryId] = useState<string | null>(existing?.categoryId ?? null);
  const [labelImageUrl, setLabelImageUrl] = useState(existing?.labelImageUrl ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [paymentStatus, setPaymentStatus] = useState<RegistrationPaymentStatus | ''>(
    existing?.paymentStatus ?? '',
  );
  const [overrideCapacity, setOverrideCapacity] = useState(false);
  const [overrideQuota, setOverrideQuota] = useState(false);
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quotaHit, setQuotaHit] = useState(false);

  useEffect(() => setQuotaHit(false), [categoryId]);

  const category = categories.find((c) => c.id === categoryId) ?? null;
  // A wine typed into an older app has no structured draft, and picking a
  // category pre-fills the colour; that alone is not a request to clear the
  // member's wine. Only an emptied structured wine sends `wine: null`.
  const wineDirty = isDraftEmpty(wine)
    ? !isDraftEmpty(initialWine)
    : JSON.stringify(wine) !== JSON.stringify(initialWine);

  const save = useMutation({
    mutationFn: async () => {
      if (!existing) {
        if (!person?.personId) throw new ApiError(400, 'person_required', 'Pick a person from the list.');
        const body: AdminRegisterBody = {
          personId: person.personId,
          categoryId: categoryId ?? undefined,
          labelImageUrl: labelImageUrl.trim() || undefined,
          notes: notes.trim() || undefined,
          paymentStatus: paymentStatus
            ? (paymentStatus as (typeof CREATE_PAYMENT_STATUSES)[number])
            : undefined,
          overrideCapacity,
          overrideQuota,
          notify,
        };
        if (!isDraftEmpty(wine)) body.wine = finishedWine(wine, worldScope);
        return rsvpApi.register(eventId, body);
      }
      const body: AdminUpdateRegistrationBody = {};
      if (status !== existing.status) body.status = status;
      if (wineDirty) body.wine = isDraftEmpty(wine) ? null : finishedWine(wine, worldScope);
      if (categoryId !== existing.categoryId) body.categoryId = categoryId;
      if (labelImageUrl.trim() !== (existing.labelImageUrl ?? ''))
        body.labelImageUrl = labelImageUrl.trim() || null;
      if (notes !== (existing.notes ?? '')) body.notes = notes || null;
      if (paymentStatus && paymentStatus !== existing.paymentStatus) body.paymentStatus = paymentStatus;
      if (overrideCapacity) body.overrideCapacity = true;
      if (overrideQuota) body.overrideQuota = true;
      return rsvpApi.updateRegistration(eventId, existing.id, body);
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'category_full') setQuotaHit(true);
      setError(e instanceof ApiError || e instanceof Error ? e.message : 'Could not save');
    },
  });

  const paymentOptions = existing ? REGISTRATION_PAYMENT_STATUSES : CREATE_PAYMENT_STATUSES;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
        {!existing && (
          <div>
            <span className="mb-1 block text-sm text-cream-200">
              Who<span className="text-wine-300"> *</span>
            </span>
            <PersonPicker value={person} onChange={setPerson} placeholder="Start typing a name" />
            <p className="mt-1 text-xs text-cream-300/50">
              Bringing a guest? Add them as a person first (Members → Invite, with a guest member
              type), then register them here.
            </p>
          </div>
        )}

        {existing && (
          <label className="block">
            <span className="mb-1 block text-sm text-cream-200">Status</span>
            <select
              className="field"
              value={status}
              onChange={(e) => setStatus(e.target.value as RegistrationStatus)}
            >
              {REGISTRATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            {status === 'cancelled' && existing.status !== 'cancelled' && (
              <span className="mt-1 block text-xs text-warning">
                Cancelling frees the seat for the waitlist. A paid seat becomes &ldquo;refund
                due&rdquo; unless it&apos;s inside the cutoff.
              </span>
            )}
          </label>
        )}

        {categories.length > 0 && (
          <label className="block">
            <span className="mb-1 block text-sm text-cream-200">Category</span>
            <select
              className="field"
              value={categoryId ?? ''}
              onChange={(e) => setCategoryId(e.target.value || null)}
            >
              <option value="">None yet</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.remaining == null
                    ? ''
                    : c.full && c.id !== existing?.categoryId
                      ? ' (full)'
                      : ` (${c.remaining} left)`}
                </option>
              ))}
            </select>
          </label>
        )}

        <div>
          <h3 className="mb-2 text-sm uppercase tracking-wide text-gold-400">Wine</h3>
          {existing && !existing.wine && existing.wineName && (
            <p className="mb-3 text-sm text-cream-300/70">
              They typed: <span className="text-cream-100">{existing.wineName}</span>
            </p>
          )}
          <StructuredWineFields
            value={wine}
            onChange={setWine}
            worldScope={worldScope}
            categoryColor={category?.color}
            forSomeoneElse
          />
          {existing?.wine && (
            <button
              type="button"
              className="btn-quiet mt-2 px-0 text-xs"
              onClick={() => setWine({})}
            >
              Clear the wine
            </button>
          )}
        </div>

        <div>
          <span className="mb-1 block text-sm text-cream-200">Label photo</span>
          <ImageInput value={labelImageUrl} onChange={setLabelImageUrl} />
        </div>

        <label className="block">
          <span className="mb-1 block text-sm text-cream-200">Notes</span>
          <textarea
            className="field min-h-[60px]"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm text-cream-200">Payment</span>
          <select
            className="field"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as RegistrationPaymentStatus | '')}
          >
            {!existing && (
              <option value="">
                Default ({requiresPayment ? 'Unpaid' : 'No payment needed'})
              </option>
            )}
            {paymentOptions.map((s) => (
              <option key={s} value={s}>
                {REGISTRATION_PAYMENT_LABELS[s]}
              </option>
            ))}
          </select>
        </label>

        <div className="space-y-2 rounded-md border border-ink-800 p-3">
          <label className="flex items-start gap-2 text-sm text-cream-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-gold-500"
              checked={overrideCapacity}
              onChange={(e) => setOverrideCapacity(e.target.checked)}
            />
            <span>
              Give them a seat even if the tasting is full
              <span className="block text-xs text-cream-300/50">
                Otherwise they go on the waitlist.
              </span>
            </span>
          </label>
          {categories.length > 0 && (
            <label className="flex items-start gap-2 text-sm text-cream-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-gold-500"
                checked={overrideQuota}
                onChange={(e) => setOverrideQuota(e.target.checked)}
              />
              <span className={quotaHit ? 'text-warning' : undefined}>
                Allow it even if the category is full
              </span>
            </label>
          )}
          {!existing && (
            <label className="flex items-start gap-2 text-sm text-cream-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-gold-500"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Send them the usual RSVP confirmation
            </label>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-wine-300">
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-3">
        <Button variant="quiet" onClick={onCancel}>
          Close
        </Button>
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save' : 'Register'}
        </Button>
      </div>
    </form>
  );
}

/** The structured wine, or an error naming what's missing. */
function finishedWine(wine: StructuredWineDraft, scope: EventWorldScope) {
  const done = completeWine(wine, scope);
  if (!done.wine) throw new Error(`The wine needs: ${done.missing.join(', ')}.`);
  return done.wine;
}
